import { RootState } from "app/store";
import MoneyRequestCard from "components/card/MoneyRequestCard";
import Menu from "components/common/Menu";
import { SideBarTabs } from "constants/tabs";
import useRequest from "hook/useRequest";
import { useTabActive } from "hook/useTabActive";
import React, { useEffect, useState } from "react";
import { IoArrowBackOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { setMoneyRequest } from "reducers/requestReducer";
import { CommonStyles } from "styles/CommonStyles";
import { MoneyRequestType } from "types/loadedData";
import PageHeader from "components/common/PageHeader";
import EmptyState from "components/common/EmptyState";
import LoadMoreButton from "components/common/LoadMoreButton";

function MoneyRequestPage() {
  const location = useLocation();
  const selectedRequestId = String(location.state?.moneyRequestId || "");
  const { getMoneyRequests, getUserMoneyRequests, updateMoneyRequest } =
    useRequest();
  const { moneyRequests, moneyRequest } = useSelector(
    (state: RootState) => state.request
  );
  const { moneyRequestReload } = useSelector(
    (state: RootState) => state.reload
  );
  const dispatch = useDispatch();
  const [loadingMoreRequests, setLoadingMoreRequests] =
    useState<boolean>(false);
  const [localRequests, setLocalRequests] = useState<MoneyRequestType[]>([]);
  const [amount, setAmount] = useState<string | number>(
    moneyRequest ? moneyRequest.amount : ""
  );

  const loadData = async () => {
    await getMoneyRequests({}, {});
  };

  const loadMoneyRequets = async () => {
    setLocalRequests([]);
    if (!moneyRequest) return;
    const requests = await getUserMoneyRequests({
      user: moneyRequest.user._id,
    });

    if (requests) {
      setLocalRequests(requests);
    }
  };

  useTabActive(SideBarTabs.MoneyRequest.name);

  useEffect(() => {
    if (moneyRequest) {
      if (moneyRequest.amount) {
        setAmount(moneyRequest.amount);
      } else {
        setAmount("");
      }
    } else {
      setAmount("");
    }
  }, [moneyRequest]);

  useEffect(() => {
    loadData();
  }, [moneyRequestReload]);

  useEffect(() => {
    loadMoneyRequets();
  }, [moneyRequest]);

  useEffect(() => {
    if (!selectedRequestId || !moneyRequests.length) return;
    const selectedRequest = moneyRequests.find(
      (request) => String(request._id) === selectedRequestId
    );
    if (selectedRequest) dispatch(setMoneyRequest(selectedRequest));

    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById(`money-request-${selectedRequestId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [dispatch, moneyRequests, selectedRequestId]);

  const handleback = async () => {
    dispatch(setMoneyRequest(null));
  };

  const handleLoadMoreRequests = async () => {
    await getMoneyRequests(
      { setLoading: setLoadingMoreRequests, offset: moneyRequests.length },
      { more: true }
    );
  };

  const handeStatusUpdate = async (type: "Approved" | "Denied") => {
    if (!moneyRequest) return;
    await updateMoneyRequest({
      amount: amount,
      requestStatus: type,
      id: moneyRequest?._id,
    });
  };

  return (
    <div className={`${CommonStyles.pageWrapper}`}>
      <Menu />
      <PageHeader
        title="Money requests"
        description="Review payment proof and approve or deny balance requests."
      />
      <div className={ClassName.container}>
        <section
          className={`${ClassName.reportSection} ${
            moneyRequest ? "hidden lg:flex" : "flex"
          }`}
        >
          <div className={ClassName.reportListContainer}>
            <div className={ClassName.reportList}>
              {moneyRequests.map((request, index) => {
                const requestId = String(request._id || index);
                return (
                  <div
                    id={`money-request-${requestId}`}
                    key={requestId}
                    className={
                      String(moneyRequest?._id || "") === requestId
                        ? ClassName.selectedTarget
                        : ClassName.target
                    }
                  >
                    <MoneyRequestCard request={request} />
                  </div>
                );
              })}
              {!moneyRequests.length ? (
                <EmptyState
                  title="No money requests"
                  description="New balance requests will appear here."
                />
              ) : null}
              {moneyRequests.length > 19 && (
                <LoadMoreButton
                  onClick={handleLoadMoreRequests}
                  loading={loadingMoreRequests}
                />
              )}
            </div>
          </div>
        </section>

        {moneyRequest && (
          <section className={ClassName.callSection}>
            <IoArrowBackOutline
              size={30}
              className="mb-0 flex lg:hidden"
              onClick={handleback}
            />
            <div className="mt-4 flex flex-1 flex-col gap-6 lg:flex-row">
              <div className="flex flex-1 flex-col">
                <span className="text-lg font-semibold">{moneyRequest.user.name}</span>
                <span className="font-bold mt-2">
                  {moneyRequest.user.phoneNumber}
                </span>
                <img
                  src={moneyRequest.screenshot}
                  alt="Payment screenshot"
                  className="my-4 max-h-[420px] w-full rounded-xl border border-slate-200 bg-slate-50 object-contain"
                />
                <input
                  type="text"
                  disabled={
                    moneyRequest.status === "Approved" ||
                    moneyRequest.status === "Denied"
                  }
                  maxLength={9}
                  value={amount}
                  onChange={(e) => {
                    const numericValue = e.target?.value?.replace(
                      /[^0-9]/g,
                      ""
                    );
                    setAmount(numericValue);
                  }}
                  placeholder="Enter amount"
                  className="flex h-12 w-full rounded-lg border border-slate-300 px-4"
                />
                {moneyRequest.status === "Requested" && (
                  <div className="flex w-full items-center gap-[5%] mt-6">
                    <button
                      className="flex flex-1 h-[52px] items-center justify-center bg-[#52C93F] rounded-md text-white font-bold"
                      onClick={() => handeStatusUpdate("Approved")}
                    >
                      Approve
                    </button>
                    <button
                      className="flex flex-1 h-[52px] items-center justify-center bg-[#E73E3E] rounded-md text-white font-bold"
                      onClick={() => handeStatusUpdate("Denied")}
                    >
                      Deny
                    </button>
                  </div>
                )}
              </div>
              <div className="flex flex-1 lg:mt-0">
                <div className="h-fit w-full rounded-xl border border-slate-200 p-4">
                  <span className="mb-4 block text-lg font-bold">
                    Current balance:{" "}
                    <span className="text-primary">
                      {moneyRequest.user.balance || 0} Br
                    </span>
                  </span>
                  <div className="w-full flex border-b py-2 text-secondaryText">
                    <span className="flex flex-1">Status</span>
                    <span className="flex flex-1">Amount</span>
                  </div>
                  {!localRequests.length ? (
                    <p className="py-6 text-center text-sm text-secondaryText">
                      No previous requests.
                    </p>
                  ) : null}
                  {localRequests.map((request, index) => (
                    <div
                      className="w-full flex border-b py-2 text-secondaryText"
                      key={String(request._id || index)}
                    >
                      <div className="flex flex-1 items-center gap-2">
                        <div
                          className={`w-4 h-4 rounded-full ${
                            request.status === "Approved"
                              ? "bg-[#52C93F]"
                              : request.status === "Denied"
                              ? "bg-[#E73E3E]"
                              : "bg-[#F8C40D]"
                          }`}
                        ></div>
                        <span>{request.status}</span>
                      </div>
                      <span className="flex flex-1">
                        {request.amount ? request.amount + " Br" : "---"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default MoneyRequestPage;

const ClassName = {
  container: "flex flex-1 flex-col gap-5 text-primaryText lg:flex-row",
  reportSection: "min-w-0 flex-col lg:w-[360px]",
  reportListContainer:
    "flex w-full flex-col rounded-2xl border border-slate-200 bg-white p-2",
  reportList: "flex max-h-[calc(100vh-190px)] min-h-56 flex-col overflow-y-auto",
  callSection:
    "flex min-w-0 flex-1 flex-col overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4",
  target: "rounded-xl transition-colors",
  selectedTarget: "rounded-xl bg-red-50 ring-2 ring-primary/40 transition-colors",
};
